/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { withRouter, useRouter } from 'next/router';

/**
 * Constants
 */
import ROUTES from '@/src/constants/routes';

/**
 * Components
 */
import Button from '@pedidosya/web-fenix/system/Button';
import Form, { FormBody, FormFooter } from '@/src/components/Form';
import Layout from '@/src/components/Layout';
import PasswordRulesInput from '@/src/components/PasswordRulesInput';

/**
 * Hooks
 */
import useIsDesktop from '@/src/hooks/useIsDesktopLayout';
import useToast from '@/src/hooks/useToast';
import { useStrongPasswordRules } from './hooks/useStrongPasswordRules';
import { useResetPassword } from './hooks/useResetPassword';

import messages from './messages';

const InputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const ResetPassword = ({ intl: { formatMessage } }) => {
  const isDesktop = useIsDesktop();
  const router = useRouter();

  const { hash } = router.query;

  const [password, setPassword] = useState('');
  const [rules, setRules] = useState([]);
  const [isFormValid, setFormValid] = useState(false);
  const toast = useToast();

  const { mutateAsync: rulesMutateAsync, isLoading: isLoadingRules } =
    useStrongPasswordRules();
  const {
    mutateAsync: resetPasswordMutateAsync,
    isLoading: isLoadingResetPassword,
  } = useResetPassword();

  const goToHome = useCallback(() => {
    router.push({
      pathname: ROUTES.HOME,
      query: { ...router.query },
    });
  }, [router]);

  const handleChangePassword = (value) => {
    setPassword(value);

    const validateRules = rules.map((rule) => {
      const rgx = new RegExp(rule.rule);

      return {
        ...rule,
        checked: rgx.test(value),
      };
    });

    setRules(validateRules);

    const hasUncheckedRule = validateRules.some(
      (rule) => rule.checked === false,
    );
    setFormValid(!hasUncheckedRule);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isFormValid) {
      const { error, success } = await resetPasswordMutateAsync({
        password,
        hash,
      });

      if (error) {
        toast.error(`${formatMessage(messages.submitErrorMessage)}`);
      }

      if (success) {
        toast.success(`${formatMessage(messages.submitSuccessMessage)}`);
        goToHome();
      }
    }
  };

  const getStrongPasswordRules = useCallback(async () => {
    const { data, error } = await rulesMutateAsync();

    if (error) {
      toast.error(`${formatMessage(messages.rulesErrorResponse)}`);
      goToHome();
    }

    if (data) {
      setRules(data.map((rule) => ({ ...rule, checked: false })));
    }
  }, []);

  useEffect(() => {
    if (!hash) {
      goToHome();
    } else {
      getStrongPasswordRules();
    }
  }, []);

  useEffect(() => {
    if (!isDesktop) {
      document.body.style.backgroundColor = '#FFFFFF';
    }

    return () => {
      document.body.style.backgroundColor = null;
    };
  }, [isDesktop]);

  const isButtonDisabled = !isFormValid || isLoadingResetPassword;

  return (
    <Layout
      title={formatMessage(messages.title)}
      subtitle={formatMessage(messages.subtitle)}
      isLoading={isLoadingRules || isLoadingResetPassword}
      onBack={goToHome}
    >
      <Form onSubmit={handleSubmit}>
        <FormBody>
          <InputsContainer>
            <PasswordRulesInput
              label={formatMessage(messages.passwordLabel)}
              value={password}
              onChange={(ev) => handleChangePassword(ev.target.value)}
              rules={rules}
              successMessage={formatMessage(messages.rulesSuccessMessage)}
            />
          </InputsContainer>
        </FormBody>
        <FormFooter>
          <Button
            label={formatMessage(messages.submit)}
            size="large"
            type="submit"
            state={isButtonDisabled ? 'disabled' : 'enabled'}
            disabled={isButtonDisabled}
            fullWidth
          />
        </FormFooter>
      </Form>
    </Layout>
  );
};

export { ResetPassword as ResetPasswordTests };
export default withRouter(injectIntl(ResetPassword));
